import cn from "classnames"
import React, { useContext, useEffect, useRef } from "react"
import { connect } from "react-redux"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { type TransitionProps } from "react-transition-group/Transition"
import useMedia from "@/common/hooks/use_media"
import tailwindTheme from "@/common/styles/theme.module.css"
import type Sign from "@/signomatic/app/sign"
import ColorPicker from "@/signomatic/components/color_picker"
import ObjectBorderPicker from "@/signomatic/components/object_border_picker"
import ToolboxItem, {
  type ToolboxItemOwnProps,
  type ToolboxItemStateProps,
  type ToolboxKey,
} from "@/signomatic/components/toolbox_item"
import { SignContext, ActiveButtonContext } from "@/signomatic/contexts"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

const ToolboxTransition = (props: Partial<TransitionProps>) => (
  <CSSTransition timeout={{ enter: 300, exit: 300 }} {...(props as TransitionProps)} />
)

interface ToolboxOwnProps {
  children: React.ReactNode
  overflow?: boolean
  className?: string
  itemProps?: ToolboxItemOwnProps[]
  tKey?: ToolboxKey
  hideHeaderBar?: boolean
  labelKey?: string
}

interface ToolboxStateProps {
  subToolbox: string | null
  subToolboxStack: string[]
  subToolboxTarget: string | null
}

export interface ToolboxDispatchProps {
  setToolbox: typeof actions.setToolbox
  resetToolbox: typeof actions.resetToolbox
  setToolboxHeight: typeof actions.setToolboxHeight
}

type ToolboxProps = ToolboxOwnProps & ToolboxStateProps & ToolboxDispatchProps

interface ToolboxWrapperProps {
  desktop: boolean
  activeButton: React.RefObject<HTMLDivElement> | undefined
  sign: Sign
}

type ToolboxWrappedProps = ToolboxProps & ToolboxWrapperProps

const Toolbox: React.FC<ToolboxWrappedProps> = ({
  children,
  overflow,
  className,
  itemProps,
  subToolbox,
  subToolboxStack,
  subToolboxTarget,
  setToolbox,
  resetToolbox,
  setToolboxHeight,
  desktop,
  sign,
  tKey,
  hideHeaderBar,
  labelKey,
}) => {
  const toolboxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const overlayClick = (e: MouseEvent) => {
      if (e.target instanceof Element) {
        if (
          !e.target.closest(".toolbox") &&
          !e.target.closest(".canvas") &&
          !e.target.closest(".toolbox-strip.menu_button.active") &&
          !e.target.closest(".signomatic-modal") &&
          !e.target.closest(".react-select") &&
          !e.target.closest(".signomatic-basket") &&
          !e.target.closest("#signomatic-basket") &&
          !e.target.closest("#video-modal") &&
          e.target !== document.body
        ) {
          resetToolbox()
        }
      }
    }

    document.addEventListener("click", overlayClick, { capture: true })

    return () => {
      document.removeEventListener("click", overlayClick, { capture: true })
    }
  }, [resetToolbox])

  const fullChildren = (sign: Sign, desktop = false) => {
    const toolboxes = subToolboxStack?.concat(subToolbox || [])
    const target = subToolboxTarget
    const updatedChildren = React.Children.toArray(children)

    if (toolboxes?.includes("objectborder")) {
      updatedChildren.push(<ObjectBorderPicker sign={sign} />)
    }

    if (toolboxes?.includes("color") && (!desktop || !toolboxes?.includes("customcolor")) && target !== "object") {
      updatedChildren.push(<ColorPicker sign={sign} />)
    }

    if (toolboxes?.includes("customcolor")) {
      updatedChildren.push(<ColorPicker sign={sign} custom />)
    }

    return updatedChildren
  }

  const toolboxItemProps = (index: number): ToolboxItemOwnProps => ({
    overflow,
    className,
    sign,
    subToolbox,
    tKey,
    hideHeaderBar,
    labelKey,
    ...(itemProps ? itemProps[index] : {}),
  })

  const toolboxItemStateProps = (): ToolboxItemStateProps => ({
    setToolbox,
    setToolboxHeight,
  })

  const renderMobile = (sign: Sign) => {
    const children = fullChildren(sign)
    const childIndex = children.length - 1

    return (
      <TransitionGroup component={null}>
        <ToolboxTransition key={children.length} classNames="toolbox-animation">
          <ToolboxItem {...toolboxItemProps(childIndex)} {...toolboxItemStateProps()} index={childIndex} mobile>
            {children[childIndex]}
          </ToolboxItem>
        </ToolboxTransition>
      </TransitionGroup>
    )
  }

  const renderDesktop = (sign: Sign) => {
    const children = fullChildren(sign, true)
    const inPlaceChildren = children.splice(2)

    return (
      <React.Fragment>
        {React.Children.map(children, (child, i) => (
          <ToolboxItem
            {...toolboxItemProps(i)}
            {...toolboxItemStateProps()}
            forwardedRef={i === 0 ? toolboxRef : null}
            index={i}
          >
            {child}
          </ToolboxItem>
        ))}
        {inPlaceChildren.length > 0 && (
          <ToolboxItem
            index={children.length + Math.max(0, inPlaceChildren.length - 1)}
            sign={sign}
            {...toolboxItemStateProps()}
          >
            {inPlaceChildren[inPlaceChildren.length - 1]}
          </ToolboxItem>
        )}
      </React.Fragment>
    )
  }

  return <>{desktop ? renderDesktop(sign) : renderMobile(sign)}</>
}

const ToolboxWrapper: React.FC<ToolboxProps> = props => {
  const sign = useContext(SignContext)
  const activeButton = useContext(ActiveButtonContext)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensLg})`)

  if (!sign) {
    throw new Error("Sign instance undefined")
  }

  return (
    <div
      className={cn("toolbox-container pointer-events-none z-30 lg:flex lg:items-center lg:border-b-1 lg:border-l-1", {
        left: props.subToolbox,
        right: !props.subToolbox,
      })}
    >
      <Toolbox desktop={desktop} activeButton={activeButton} sign={sign} {...props} />
    </div>
  )
}

export default connect<ToolboxStateProps, ToolboxDispatchProps, ToolboxOwnProps>(
  ({ subToolbox, subToolboxStack, subToolboxTarget }: State) => ({
    subToolbox,
    subToolboxStack,
    subToolboxTarget,
  }),
  {
    setToolbox: actions.setToolbox,
    resetToolbox: actions.resetToolbox,
    setToolboxHeight: actions.setToolboxHeight,
  }
)(ToolboxWrapper)
